/**
 * The parameter registry — pure logic, no React, no `window`, no network.
 *
 * ADR 2000, as amended by the owner's ruling of 2026-11-02 (bata-platform ADR 9057). A row's
 * reset restores the key's **Default**: `ParameterKeyEntry.guidelineDefault` on the catalogue
 * read — the reading code's own default, or a guideline the team drafted and the owner signs —
 * never a value invented here. A key with no Default cannot be reset, and says so. The previous
 * value the change in force replaced is no longer what a reset restores ("not much point
 * remembering the previous value"). A reset is a proposal (`POST /registry/changes`), never a
 * direct write: `17` §4 makes a parameter change a two-step governed act, and every field of the
 * proposal (owner, `reviewDueOn`, `validFrom`, `validTo`) comes off the record rather than being
 * invented here.
 *
 * `22-colombia-pilot-improvements.md` UI6 asks for "an inspectable, resettable table, not
 * a form": Section / Name / Value, grouped, collapsible, with an explicit reset. This
 * reads `GET /api/v1/registry/parameters` (`ParameterRow`, `as_of` required), which
 * carries the validity window and the `changeId` of the change set that put the row in
 * force — the only published route to what the value was before.
 */

import type { TFunction } from 'i18next';

import type {
  GuidelineDefaultModel,
  ParameterKeyEntry,
  ParameterRow,
  ProposeChangeParams,
  RevisionRequest,
} from '@/api/generated';
import { MINIMUM_RATIONALE_WORDS, parameterValue, rationaleWordCount, type ParameterValue } from '@/planning';

// ── sections ─────────────────────────────────────────────────────────────────

/**
 * The section a parameter belongs to: the first dot-segment of its key.
 *
 * A DERIVATION over what the contract publishes, not a table maintained here. The keys
 * the estate holds are namespaced by the thing they govern — `markdown.*`,
 * `objective.*`, `allocation.*` — so the namespace is the section a planner already
 * reasons in, and a namespace that has never been seen before groups itself correctly on
 * the day it lands. A hand-kept section table would have shown a new key as unsectioned,
 * or worse, filed it under a neighbour (the rule `docs/adr/0054` states: a key with a
 * wrong label is worse than a key with none).
 *
 * A key with no dot has no section. It is returned as `null` rather than folded into a
 * `general` bucket that does not exist in the register.
 */
export function sectionOf(parameterKey: string): string | null {
  const index = parameterKey.indexOf('.');
  return index <= 0 ? null : parameterKey.slice(0, index);
}

/** The id the unsectioned group carries. Never a real section, because `''` is not a key. */
export const UNSECTIONED = '';

/** A row's identity within the register: the key AND its scope, never the key alone. */
export function rowId(row: ParameterRow): string {
  return `${row.parameterKey}\u0000${row.scopeDimension}\u0000${row.scopeValue}`;
}

// ── the search above the register (bata-ui#132 Step 3) ─────────────────────────

/**
 * Whether one row answers the planner's search: the query, trimmed and case-folded, found
 * anywhere in the parameter key or the owner.
 *
 * Client-side over rows already in hand, and honestly so: `useApiCollection` reads every
 * page of `GET /registry/parameters` (`collectPages` refuses rather than truncates — the
 * argument `src/planning/data-table.tsx` makes for its own column filters), so the
 * rows filtered here are the whole register on the date, not a prefix of it. A blank query
 * matches every row — "no search" is not "a search for nothing".
 *
 * Case-folded with `toLowerCase` rather than a locale collator: the key is a code and the
 * owner a name the register recorded, and a planner typing `ALLOC` means `allocation.*`
 * whatever the interface language is.
 */
export function matchesParameterSearch(row: ParameterRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return row.parameterKey.toLowerCase().includes(needle)
    || row.owner.toLowerCase().includes(needle);
}

/** The rows a search keeps, in the order they were given. */
export function filterParameters(
  rows: readonly ParameterRow[],
  query: string,
): readonly ParameterRow[] {
  return query.trim() === '' ? rows : rows.filter((row) => matchesParameterSearch(row, query));
}

// ── values ───────────────────────────────────────────────────────────────────

// `ParameterValue` / `parameterValue` are shared with Simulate's levers (`docs/adr/9024`).
export { parameterValue, type ParameterValue };

// ── the Default a reset restores (bata-platform ADR 9057) ─────────────────────

/**
 * A parameter change item's `targetKey` as the row address it names, or `null` for any other
 * shape. bata-platform `changeset.parameter_target_key` joins exactly three parts with `|` and
 * refuses a part containing the separator, so the split is unambiguous; anything that is not
 * three parts is not a parameter row's address and is not guessed at.
 */
// TODO(bata-ui#198): replace with `splitTargetKey` from `parameter-editing.ts` once #198 lands.
export function parameterTargetAddress(targetKey: string): {
  readonly parameterKey: string;
  readonly scopeDimension: string;
  readonly scopeValue: string;
} | null {
  const parts = targetKey.split('|');
  if (parts.length !== 3) return null;
  const [parameterKey, scopeDimension, scopeValue] = parts as [string, string, string];
  return parameterKey === '' ? null : { parameterKey, scopeDimension, scopeValue };
}

/**
 * A key's Default, as the catalogue publishes it: a number or a text, whether the owner has signed
 * it, and where it comes from. `draft` is a guideline the team drafted that the owner has not yet
 * signed — the register marks it «Draft — to be signed». A code default is always signed.
 */
export interface KeyDefault {
  readonly value: { readonly kind: 'numeric'; readonly value: number } | { readonly kind: 'text'; readonly value: string };
  readonly draft: boolean;
  readonly source: GuidelineDefaultModel['source'];
  readonly evidenceRef: string | null;
  readonly market: string | null;
  readonly asOf: string | null;
}

/**
 * The key's Default, or `null` where the catalogue publishes none — the blank column (owner: "I'd
 * much rather have nothing if we can't get a guideline factory default"). A published Default that
 * carries neither a number nor a text, or both, is not one: it is not guessed at.
 */
export function keyDefault(entry: ParameterKeyEntry | null | undefined): KeyDefault | null {
  const found = entry?.guidelineDefault ?? null;
  if (found === null) return null;
  const numeric = found.numericValue ?? null;
  const text = found.textValue ?? null;
  if ((numeric === null) === (text === null)) return null;
  return {
    value: numeric !== null ? { kind: 'numeric', value: numeric } : { kind: 'text', value: text ?? '' },
    draft: found.status === 'draft',
    source: found.source,
    evidenceRef: found.evidenceRef ?? null,
    market: found.market ?? null,
    asOf: found.asOf ?? null,
  };
}

/**
 * Where a Default comes from, in words — one switch for every place that says it (the inspector and the row's draft mark), exhaustive over
 * the sources the catalogue publishes: the reading code's own, an ADR, a parameter card, a measurement on the estate's own data
 * (`analysis`), or a market's current setting.
 */
export function defaultSourceText(found: Pick<KeyDefault, 'source' | 'evidenceRef' | 'market' | 'asOf'>, t: TFunction): string {
  const ref = found.evidenceRef ?? '';
  switch (found.source) {
    case 'code': return t('foundations.parameters.default.source.code');
    case 'market_value': return t('foundations.parameters.default.source.marketValue', { market: found.market ?? '', asOf: found.asOf ?? '' });
    case 'adr': return t('foundations.parameters.default.source.adr', { ref });
    case 'card': return t('foundations.parameters.default.source.card', { ref });
    case 'analysis': return t('foundations.parameters.default.source.analysis', { ref });
  }
}

/** The Default's number when it is one — what the range track marks. */
export function defaultNumber(found: KeyDefault | null): number | null {
  return found !== null && found.value.kind === 'numeric' ? found.value.value : null;
}

/** Whether the value in force IS the Default — then a reset would restore nothing. */
export function equalsDefault(value: ParameterValue, found: KeyDefault | null): boolean {
  if (found === null) return false;
  if (value.kind === 'numeric') return found.value.kind === 'numeric' && found.value.value === value.value;
  if (value.kind === 'text') return found.value.kind === 'text' && found.value.value === value.value;
  return false;
}

// ── composing a reset ────────────────────────────────────────────────────────

/** Why one row cannot be reset. Every one of these is shown to the planner, never swallowed. */
export type ResetRefusal =
  | 'no_default'
  | 'default_not_numeric'
  | 'default_not_text'
  | 'value_not_resettable'
  | 'no_owner'
  | 'no_review_date'
  | 'no_validity_window'
  | 'owners_differ'
  | 'nothing_to_reset';

export interface ResetCandidate {
  readonly row: ParameterRow;
  /** The key's Default (`keyDefault`), or `null` where the catalogue publishes none. */
  readonly keyDefault: KeyDefault | null;
}

export type RevisionOutcome =
  | { readonly ok: true; readonly revision: RevisionRequest }
  | { readonly ok: false; readonly refusal: ResetRefusal };

/**
 * One row's reset, as a revision — or the reason there is not one.
 *
 * `validFrom` is the caller's as-at date, which is the date the table is a statement
 * about. It is a parameter rather than a clock read so this stays pure and so the
 * proposal is dated the same day the planner was looking at, not the day the request
 * happened to be dispatched.
 */
export function resetRevision(candidate: ResetCandidate, asOf: string): RevisionOutcome {
  const { row, keyDefault: found } = candidate;
  if (found === null) return { ok: false, refusal: 'no_default' };
  if (row.owner.trim() === '') return { ok: false, refusal: 'no_owner' };
  if (row.reviewDate.trim() === '') return { ok: false, refusal: 'no_review_date' };
  if (row.validTo.trim() === '') return { ok: false, refusal: 'no_validity_window' };

  const current = parameterValue(row);
  const base: Omit<RevisionRequest, 'numericValue' | 'textValue'> = {
    parameterKey: row.parameterKey,
    scopeDimension: row.scopeDimension,
    scopeValue: row.scopeValue,
    validFrom: asOf,
    validTo: row.validTo,
  };

  // The half put back is the one matching the row's kind. A Default of the other kind is refused
  // rather than sent: switching a threshold to a string — `1/2` is text, not a number —
  // is a change of type the register's engines would not read, and choosing a half is the register's.
  if (current.kind === 'numeric') {
    if (found.value.kind !== 'numeric') return { ok: false, refusal: 'default_not_numeric' };
    return { ok: true, revision: { ...base, numericValue: found.value.value, textValue: null } };
  }
  if (current.kind === 'text') {
    if (found.value.kind !== 'text') return { ok: false, refusal: 'default_not_text' };
    return { ok: true, revision: { ...base, numericValue: null, textValue: found.value.value } };
  }
  // Absent or ambiguous: there is no column to put the Default back into without
  // deciding which kind of value this row holds, and that decision is the register's.
  return { ok: false, refusal: 'value_not_resettable' };
}

/**
 * The rationale written onto the change record.
 *
 * An audit-record datum, not UI copy — it is stored by the platform and read by an
 * approver, so it is not a catalogue key and is not translated here. `17` §3 wants a
 * rationale that says what was done and why; this one also says where from, because a
 * reviewer finding it in eighteen months should be able to tell a screen-proposed
 * rollback from a hand-authored change.
 */
export const RESET_RATIONALE = 'Restore the Default the platform catalogue publishes for each key (guidelineDefault, bata-platform ADR 9057): the code default, or the guideline drafted for the owner to sign. Proposed from the foundations parameter registry screen; no value is authored by the client.';

/** The same, when a Default restored is a draft the owner has not yet signed: the approver must know. */
export const RESET_RATIONALE_WITH_DRAFT = `${RESET_RATIONALE} At least one restored Default is a draft guideline not yet signed by the owner.`;

export type ResetComposition =
  | { readonly ok: true; readonly params: ProposeChangeParams }
  | { readonly ok: false; readonly refusal: ResetRefusal };

/**
 * A whole reset — one row or one section — as the governed change set that proposes it.
 *
 * Refuses for the SET rather than dropping the rows it cannot compose. A section reset
 * that quietly proposed four of six rows would report success over a perimeter nobody
 * chose, which is the failure `docs/adr/0043` names on the bulk approval: an auditor a
 * month later cannot reconstruct what was in the set.
 */
export function composeReset(
  candidates: readonly ResetCandidate[],
  asOf: string,
  changeId: string,
): ResetComposition {
  if (candidates.length === 0) return { ok: false, refusal: 'nothing_to_reset' };
  if (asOf.trim() === '' || changeId.trim() === '') return { ok: false, refusal: 'nothing_to_reset' };

  const owners = new Set(candidates.map((candidate) => candidate.row.owner.trim()));
  if (owners.size > 1) return { ok: false, refusal: 'owners_differ' };

  const revisions: RevisionRequest[] = [];
  for (const candidate of candidates) {
    const outcome = resetRevision(candidate, asOf);
    if (!outcome.ok) return { ok: false, refusal: outcome.refusal };
    revisions.push(outcome.revision);
  }

  const owner = candidates[0]?.row.owner ?? '';
  // The soonest commitment already on the record. Sorted as strings, which is exact for
  // ISO dates and needs no timezone this module would have to pick.
  const reviewDueOn = [...candidates]
    .map((candidate) => candidate.row.reviewDate)
    .sort()[0] ?? '';

  return {
    ok: true,
    params: {
      body: {
        changeId,
        owner,
        rationale: candidates.some((candidate) => candidate.keyDefault?.draft === true) ? RESET_RATIONALE_WITH_DRAFT : RESET_RATIONALE,
        reviewDueOn,
        revisions,
      },
    },
  };
}

/**
 * The parameters of a reset that has not been composed.
 *
 * `useApiMutation` takes its parameters at hook-call time, which is what makes the
 * idempotency key identify the DECISION (`docs/adr/0016`) — so the hook cannot be skipped
 * while the planner is still deciding. This value stands in until one is composed. It is
 * never sent: the confirmation's Reset button is unreachable until `composeReset`
 * succeeds, and its signature differs from every composed one, so it holds no key.
 */
export const UNCOMPOSED_RESET: ProposeChangeParams = {
  body: { changeId: '', owner: '', rationale: '', reviewDueOn: '', revisions: [] },
};

/** Catalogue key for a refusal. Every member of the union is named; there is no fallback. */
const REFUSAL_KEY: Readonly<Record<ResetRefusal, string>> = {
  no_default: 'foundations.parameters.refusal.noDefault',
  default_not_numeric: 'foundations.parameters.refusal.defaultNotNumeric',
  default_not_text: 'foundations.parameters.refusal.defaultNotText',
  value_not_resettable: 'foundations.parameters.refusal.valueNotResettable',
  no_owner: 'foundations.parameters.refusal.noOwner',
  no_review_date: 'foundations.parameters.refusal.noReviewDate',
  no_validity_window: 'foundations.parameters.refusal.noValidityWindow',
  owners_differ: 'foundations.parameters.refusal.ownersDiffer',
  nothing_to_reset: 'foundations.parameters.refusal.nothingToReset',
};

export function resetRefusalKey(refusal: ResetRefusal): string {
  return REFUSAL_KEY[refusal];
}

// ── composing an AUTHORED proposal — a new value, never a reset (ADR 2001) ──────────
//
// `composeReset` above puts the key's Default back. This is the other
// half doc 22 UI6 always implied and the bootstrap session that opened bata-ui#101 needed
// by hand, over `curl`: a planner AUTHORS a value — a new `exception.*` threshold, a first
// value for a key nobody has set — and that value is never invented here. `numericValue`
// and `textValue` are read from what the human typed, verbatim; this module supplies no
// default for either, on purpose: a reset restores the platform's published Default, and an
// authored value is the human's alone. See `docs/adr/2001`.

/** `17` §3, machine half: "a change record whose rationale is 'updated' is rejected at
 *  review" is a word count, checked here so a thin rationale fails before the round trip
 *  the server would fail it on anyway (`_MINIMUM_RATIONALE_WORDS`, bata-platform
 *  `services/repositories/changeset.py`). The server holds the rest — prose quality is not
 *  machine-checkable and this module does not pretend otherwise. Shared with every composer of a
 *  change set (`@/planning`, `docs/adr/9024` amendment of 2026-11-03). */
export { MINIMUM_RATIONALE_WORDS, rationaleWordCount };

export type ProposalRefusal =
  | 'no_key'
  | 'scope_value_missing'
  | 'no_owner'
  | 'no_rationale'
  | 'rationale_too_short'
  | 'no_review_date'
  | 'no_valid_from'
  | 'no_valid_to'
  | 'no_value'
  | 'value_not_numeric';

/** One authored parameter proposal, as the form holds it before it is composed. Every
 *  field is a string the human typed or an empty one — there is no field this module fills
 *  in for them, including `numericValueText`/`textValue`: ADR 2000/2001's "no default,
 *  ever" binds a fresh authoring exactly as hard as it binds a reset. */
export interface ParameterProposalDraft {
  readonly parameterKey: string;
  readonly scopeDimension: string;
  readonly scopeValue: string;
  readonly valueKind: 'numeric' | 'text';
  readonly numericValueText: string;
  readonly textValue: string;
  readonly validFrom: string;
  readonly validTo: string;
  readonly owner: string;
  readonly rationale: string;
  readonly reviewDueOn: string;
  /** Optional for P7 (`ProposeRequest.evidenceRef` is nullable) — required for P2's
   *  `ProposeObjectiveRequest`, enforced in `objective-config.ts` instead. */
  readonly evidenceRef: string;
}

export type ParameterProposalComposition =
  | { readonly ok: true; readonly params: ProposeChangeParams }
  | { readonly ok: false; readonly refusal: ProposalRefusal };

/** A blank draft — the form's initial state, and what `UNCOMPOSED_PROPOSAL` mirrors below. */
export const BLANK_PROPOSAL_DRAFT: ParameterProposalDraft = {
  parameterKey: '',
  scopeDimension: '',
  scopeValue: '',
  valueKind: 'numeric',
  numericValueText: '',
  textValue: '',
  validFrom: '',
  validTo: '',
  owner: '',
  rationale: '',
  reviewDueOn: '',
  evidenceRef: '',
};

/**
 * One authored parameter revision, as the governed change set that proposes it — or the
 * reason it cannot be composed. Refuses for the field it can name rather than reporting a
 * generic failure, so the form can point at the one control the planner still has to fill.
 */
export function composeParameterProposal(
  draft: ParameterProposalDraft,
  changeId: string,
): ParameterProposalComposition {
  if (draft.parameterKey.trim() === '') return { ok: false, refusal: 'no_key' };
  // A dimension with no value is neither the estate row nor a scoped one: the pickers clear the
  // value when the dimension changes, so this is a choice left half made, not a guess to fill in.
  if (draft.scopeDimension.trim() !== '' && draft.scopeValue.trim() === '') {
    return { ok: false, refusal: 'scope_value_missing' };
  }
  if (draft.owner.trim() === '') return { ok: false, refusal: 'no_owner' };
  if (draft.rationale.trim() === '') return { ok: false, refusal: 'no_rationale' };
  if (rationaleWordCount(draft.rationale) < MINIMUM_RATIONALE_WORDS) {
    return { ok: false, refusal: 'rationale_too_short' };
  }
  if (draft.reviewDueOn.trim() === '') return { ok: false, refusal: 'no_review_date' };
  if (draft.validFrom.trim() === '') return { ok: false, refusal: 'no_valid_from' };
  if (draft.validTo.trim() === '') return { ok: false, refusal: 'no_valid_to' };

  let numericValue: number | null = null;
  let textValue: string | null = null;
  if (draft.valueKind === 'numeric') {
    if (draft.numericValueText.trim() === '') return { ok: false, refusal: 'no_value' };
    const parsed = Number(draft.numericValueText);
    if (!Number.isFinite(parsed)) return { ok: false, refusal: 'value_not_numeric' };
    numericValue = parsed;
  } else {
    if (draft.textValue.trim() === '') return { ok: false, refusal: 'no_value' };
    textValue = draft.textValue;
  }

  return {
    ok: true,
    params: {
      body: {
        changeId,
        owner: draft.owner,
        rationale: draft.rationale,
        reviewDueOn: draft.reviewDueOn,
        evidenceRef: draft.evidenceRef.trim() === '' ? null : draft.evidenceRef,
        revisions: [{
          parameterKey: draft.parameterKey,
          scopeDimension: draft.scopeDimension,
          scopeValue: draft.scopeValue,
          numericValue,
          textValue,
          validFrom: draft.validFrom,
          validTo: draft.validTo,
        }],
      },
    },
  };
}

/** Mirrors `UNCOMPOSED_RESET` above, for the same reason: `useApiMutation` takes its
 *  parameters at hook-call time, so the hook needs something to hold before a draft has
 *  composed, and its signature must differ from every real composition so it can never
 *  be sent. */
export const UNCOMPOSED_PROPOSAL: ProposeChangeParams = {
  body: { changeId: '', owner: '', rationale: '', reviewDueOn: '', revisions: [] },
};

/**
 * A proposal for ONE row of the register, pre-filled from that row (bata-ui#132 Step 3).
 *
 * The row's identity — key, scope dimension, scope value — and its governance — owner,
 * review date, expiry — come off the record, exactly as `resetRevision` takes them, and
 * `validFrom` is the as-at date the table is a statement about, for the same reason it is
 * there. The VALUE is not pre-filled, in either column: the current value is shown beside
 * the field for reference, and copying it in would make "propose" a no-op one keystroke
 * away from an accidental change (ADR 2001's "no default, ever").
 *
 * The value kind follows the row: a numeric row is re-authored as a number and a text row
 * as text, because switching a threshold to a string is a change of type the register's
 * engines would not read. A row with no value, or with both, has no kind to follow, and the
 * blank draft's kind stands until the planner picks one.
 */
export function proposalDraftFromRow(row: ParameterRow, asOf: string): ParameterProposalDraft {
  const current = parameterValue(row);
  return {
    ...BLANK_PROPOSAL_DRAFT,
    parameterKey: row.parameterKey,
    scopeDimension: row.scopeDimension,
    scopeValue: row.scopeValue,
    valueKind: current.kind === 'text' ? 'text' : current.kind === 'numeric' ? 'numeric' : BLANK_PROPOSAL_DRAFT.valueKind,
    validFrom: asOf,
    validTo: row.validTo,
    owner: row.owner,
    reviewDueOn: row.reviewDate,
  };
}

/** Whether the row fixes the value kind (numeric or text), so the form need not ask. */
export function rowFixesValueKind(row: ParameterRow): boolean {
  const kind = parameterValue(row).kind;
  return kind === 'numeric' || kind === 'text';
}

const PROPOSAL_REFUSAL_KEY: Readonly<Record<ProposalRefusal, string>> = {
  no_key: 'foundations.parameters.proposal.refusal.noKey',
  scope_value_missing: 'foundations.parameters.proposal.refusal.scopeValueMissing',
  no_owner: 'foundations.parameters.proposal.refusal.noOwner',
  no_rationale: 'foundations.parameters.proposal.refusal.noRationale',
  rationale_too_short: 'foundations.parameters.proposal.refusal.rationaleTooShort',
  no_review_date: 'foundations.parameters.proposal.refusal.noReviewDate',
  no_valid_from: 'foundations.parameters.proposal.refusal.noValidFrom',
  no_valid_to: 'foundations.parameters.proposal.refusal.noValidTo',
  no_value: 'foundations.parameters.proposal.refusal.noValue',
  value_not_numeric: 'foundations.parameters.proposal.refusal.valueNotNumeric',
};

export function proposalRefusalKey(refusal: ProposalRefusal): string {
  return PROPOSAL_REFUSAL_KEY[refusal];
}
